import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Funnel bar + "All stages" grid (approved mockups HmJobs / RecJobs / TeamJobs / AdminJobs, columns per the 4c live
// test): Sourced · Recruiter screen · AI screen · Interviews (per round) · Offer · BGV · Joined — one place per
// candidate, the same numbers in every view (job_funnel on the server). Big number = Pending + Shortlisted now in the
// stage; small lines = "N to schedule · N awaiting feedback" (AI screen, Interviews) or "N pending", "N shortlisted",
// "N out". Amber / red = a Pending candidate past the stage's target days. Hatched = the job doesn't use the stage.
// Hover = Pending by part · Shortlisted · Rejected · Withdrawn · median days. Click a number or a line = those
// candidates (onOpen(column, {tab, part, round})).
export const STAGES = [
  { key: 'sourced', head: ['Sour-', 'ced'], label: 'Sourced' },
  { key: 'recruiter_screen', head: ['Rec.', 'screen'], label: 'Recruiter screen' },
  { key: 'ai_screen', head: ['AI', 'screen'], label: 'AI screen' },
  { key: 'interviews', head: ['Inter-', 'views'], label: 'Interviews' },
  { key: 'offer', head: ['Offer'], label: 'Offer' },
  { key: 'bgv', head: ['BGV /', 'Drug'], label: 'BGV / Drug test' },
  { key: 'joined', head: ['Joined'], label: 'Joined' },
];
export const PART_LABEL = {
  added: 'Added, not screened', to_screen: 'To screen', in_progress: 'In progress', to_schedule: 'To schedule', scheduled: 'Scheduled',
  awaiting_feedback: 'Awaiting feedback', awaiting_decision: 'Awaiting HM decision', on_hold: 'On hold — HM', to_extend: 'To extend', extended: 'Extended',
};
const SHORT_WORD = { offer: 'accepted', bgv: 'cleared' };

export const STATUS = {
  draft: ['Draft', 'bg-[#F1F5F9] text-[#475569]'], in_approval: ['In approval', 'bg-[#FEF3C7] text-[#92400E]'],
  open: ['Open', 'bg-[#DCFCE7] text-[#166534]'], on_hold: ['On hold', 'bg-[#EDE9FE] text-[#5B21B6]'],
  closed: ['Closed', 'bg-[#E2E8F0] text-[#334155]'],
};
export function StatusPill({ status, sentBack }) {
  const [label, cls] = sentBack ? ['Sent back', 'bg-[#FEE2E2] text-[#991B1B]'] : (STATUS[status] || [status, 'bg-[#F1F5F9] text-[#475569]']);
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${cls}`}>{label}</span>;
}

const AGEING = { green: 'bg-[#DCFCE7] text-[#166534]', amber: 'bg-[#FEF3C7] text-[#92400E]', red: 'bg-[#FEE2E2] text-[#991B1B]' };
export function AgeingPill({ days, tone }) {
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${AGEING[tone] || AGEING.green}`}>{days === 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`}</span>;
}

// the small lines of a cell: [text, {tab, part}]
export function cellLines(key, st) {
  const out = [];
  const parts = st.parts || {};
  if (key === 'ai_screen' || key === 'interviews') {
    if (parts.to_schedule) out.push([`${parts.to_schedule} to schedule`, { tab: 'pending', part: 'to_schedule' }]);
    if (parts.awaiting_feedback) out.push([`${parts.awaiting_feedback} awaiting feedback`, { tab: 'pending', part: 'awaiting_feedback' }]);
    if (parts.awaiting_decision) out.push([`${parts.awaiting_decision} awaiting HM decision`, { tab: 'pending', part: 'awaiting_decision' }]);   // 5b
    if (parts.on_hold) out.push([`${parts.on_hold} on hold (HM)`, { tab: 'pending', part: 'on_hold' }]);
    const scheduled = parts.scheduled || 0;
    if (scheduled && !parts.to_schedule && !parts.awaiting_feedback && !parts.awaiting_decision) out.push([`${scheduled} scheduled`, { tab: 'pending', part: 'scheduled' }]);
  } else if (key !== 'joined' && st.pending) {
    out.push([`${st.pending} pending`, { tab: 'pending' }]);
  }
  if (key !== 'joined' && st.shortlisted) out.push([`${st.shortlisted} ${SHORT_WORD[key] || 'shortlisted'}`, { tab: 'shortlisted' }]);
  if (st.out) out.push([`${st.out} out`, { tab: 'out' }]);
  return out;
}

const TONE = { amber: 'ring-2 ring-[#F59E0B]', red: 'ring-2 ring-[#DC2626]' };

// Where the hover card goes: below the cell, or above it when there's no room; kept inside the window
export function tipPosition(anchor, w, h, vw, vh, gap = 8) {
  const left = Math.min(Math.max(8, anchor.left + anchor.width / 2 - w / 2), Math.max(8, vw - w - 8));
  const below = anchor.bottom + gap;
  const top = below + h <= vh - 8 || anchor.top - gap - h < 8 ? below : anchor.top - gap - h;
  return { left, top };
}

function Tip({ stage, st, anchor }) {
  const parts = Object.entries(st.parts || {}).filter(([, v]) => v);
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!anchor.current || !ref.current) return;
    setPos(tipPosition(anchor.current.getBoundingClientRect(), ref.current.offsetWidth || 260, ref.current.offsetHeight || 0, window.innerWidth, window.innerHeight));
  }, [anchor]);
  return createPortal(
    <span ref={ref} role="tooltip" style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}
      className="fixed z-[60] pointer-events-none w-[260px] bg-white border border-[#E6E9F0] rounded-[14px] shadow-[0_16px_40px_rgba(15,23,42,.18)] px-4 py-3.5 text-left text-[#0F172A]" data-testid={`tip-${stage.key}`}>
      <span className="block text-sm font-bold mb-1">{stage.label}</span>
      {stage.key === 'interviews' && st.rounds > 0 && (
        <span className="block text-xs text-[#64748B] mb-1">{Array.from({ length: st.rounds }, (_, i) => `R${i + 1} · ${(st.per_round || [])[i]?.count || 0}`).join('  ')}</span>
      )}
      {stage.key !== 'joined' && <span className="flex justify-between text-[13px] py-0.5"><span className="text-[#64748B]">Pending</span><b>{st.pending || 0}</b></span>}
      {parts.map(([k, v]) => <span key={k} className="flex justify-between text-[12px] py-0.5 pl-3"><span className="text-[#64748B]">{PART_LABEL[k] || k}</span><b>{v}</b></span>)}
      {stage.key !== 'joined' && <span className="flex justify-between text-[13px] py-0.5"><span className="text-[#64748B]">{SHORT_WORD[stage.key] ? SHORT_WORD[stage.key][0].toUpperCase() + SHORT_WORD[stage.key].slice(1) : 'Shortlisted'}</span><b>{st.shortlisted || 0}</b></span>}
      {stage.key === 'joined' && <span className="flex justify-between text-[13px] py-0.5"><span className="text-[#64748B]">Joined</span><b>{st.joined || 0}</b></span>}
      <span className="flex justify-between text-[13px] py-0.5"><span className="text-[#64748B]">Rejected</span><b>{st.rejected || 0}</b></span>
      <span className="flex justify-between text-[13px] py-0.5"><span className="text-[#64748B]">Withdrawn</span><b>{st.withdrawn || 0}</b></span>
      <span className="flex justify-between text-[13px] py-0.5"><span className="text-[#64748B]">Median days in stage</span><b>{st.median_days != null ? st.median_days : '—'}</b></span>
      {st.stuck > 0 && <span className="block text-[12px] text-[#B45309] mt-1">{st.stuck} past the {st.target_days}-day target</span>}
    </span>, document.body);
}

function Cell({ stage, st, hot, onOpen }) {
  const [hover, setHover] = useState(false);
  const anchor = useRef(null);
  if (!st || st.used === false) {
    return <span className="w-[76px] box-border rounded-[7px] py-1.5 text-center border border-dashed border-[#E2E8F0] text-[#CBD5E1]"
      style={{ background: 'repeating-linear-gradient(45deg,#F3F5F9 0 4px,#fff 4px 8px)' }} title={`${stage.label}: not used on this job`}
      data-testid={`fs-${stage.key}-na`}><b className="block text-[14px] leading-tight">–</b></span>;
  }
  const count = st.count || 0;
  const lines = cellLines(stage.key, st);
  const zero = !count && !lines.length;
  const cls = hot ? 'bg-[#0066CC] text-white' : zero ? 'bg-[#F3F5F9] text-[#94A3B8]' : 'bg-[#E8F1FB] text-[#003D82]';
  const open = (opts) => onOpen && onOpen(stage.key, opts);
  return (
    <span ref={anchor} className="relative" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <span className={`relative flex flex-col w-[76px] box-border rounded-[7px] pt-1.5 pb-[5px] text-center ${cls} ${st.tone ? TONE[st.tone] : ''}`}
        data-testid={`fs-${stage.key}`} data-tone={st.tone || ''}>
        <button type="button" onClick={() => open({ tab: st.pending ? 'pending' : (st.shortlisted || st.joined) ? 'shortlisted' : 'out' })}
          onFocus={() => setHover(true)} onBlur={() => setHover(false)} aria-label={`${stage.label}: ${count} now${st.stuck ? `, ${st.stuck} past target` : ''}`}
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0066CC]/50 rounded" data-testid={`fs-${stage.key}-count`}>
          <b className="block text-[14px] font-bold leading-tight">{count}</b></button>
        {lines.map(([t, o]) => (
          <button key={t} type="button" onClick={() => open(o)} className={`block text-[9px] font-semibold leading-tight mt-px hover:underline ${hot ? 'text-[#DCEBFA]' : 'text-[#475569]'}`}
            data-testid={`fs-${stage.key}-line`}>{t}</button>
        ))}
      </span>
      {hover && <Tip stage={stage} st={st} anchor={anchor} />}
    </span>
  );
}

export function FunnelHead() {
  return (
    <div className="flex gap-[3px]" aria-hidden="true">
      {STAGES.map((s) => <span key={s.key} className="w-[76px] text-center text-[9.5px] leading-[1.15] font-bold text-[#64748B] uppercase tracking-[.03em]">{s.head.map((h) => <span key={h} className="block">{h}</span>)}</span>)}
    </div>
  );
}

export function FunnelBar({ stages, onOpen, hotStage }) {
  return (
    <div className="flex gap-[3px]" data-testid="funnel">
      {STAGES.map((s) => <Cell key={s.key} stage={s} st={stages[s.key]} hot={hotStage === s.key} onOpen={onOpen} />)}
    </div>
  );
}

// "All stages": Pending / Shortlisted / Out per stage; interview columns grow to the most rounds any job in view uses
export function StageCells({ row, maxRounds, onOpen }) {
  const s = row.stages;
  const trio = (st, key, labels, extra = {}) => (st.used === false
    ? <td key={key + (extra.round || '')} colSpan={3} className="px-2 py-2 text-center text-[11px] text-[#94A3B8] border-l border-[#EEF1F6]"
      style={{ background: 'repeating-linear-gradient(45deg,#F8FAFC 0 4px,#fff 4px 8px)' }}>{key === 'bgv' ? 'not required' : 'not used'}</td>
    : [st.pending, st.shortlisted, st.out].map((v, i) => (
      <td key={`${key}.${extra.round || ''}.${i}`} className={`px-2 py-2 text-center text-[13px] ${i === 0 ? 'border-l border-[#EEF1F6]' : ''} ${i === 0 && st.tone ? (st.tone === 'red' ? 'text-[#B91C1C] font-bold' : 'text-[#B45309] font-bold') : ''}`}>
        <button type="button" className="hover:underline disabled:no-underline" disabled={!v}
          onClick={() => onOpen && onOpen(key, { tab: ['pending', 'shortlisted', 'out'][i], ...extra })} aria-label={`${labels[i]} ${v || 0}`}>{v || 0}</button></td>)));
  const rounds = s.interviews.rounds || 0;
  const t3 = ['Pending', 'Shortlisted', 'Out'];
  const cols = [trio(s.sourced, 'sourced', t3), trio(s.recruiter_screen, 'recruiter_screen', t3), trio(s.ai_screen, 'ai_screen', t3)];
  for (let i = 0; i < maxRounds; i += 1) {
    if (i >= rounds) {
      cols.push(<td key={`r${i}`} colSpan={3} className="px-2 py-2 text-center text-[11px] text-[#94A3B8] border-l border-[#EEF1F6]"
        style={{ background: 'repeating-linear-gradient(45deg,#F8FAFC 0 4px,#fff 4px 8px)' }}>{rounds === 0 && i === 0 ? 'not used' : `no round ${i + 1}`}</td>);
    } else {
      cols.push(trio((s.interviews.per_round || [])[i] || {}, 'interviews', ['Pending', 'Passed', 'Out'], { round: i + 1 }));
    }
  }
  cols.push(trio(s.offer, 'offer', ['Pending', 'Accepted', 'Out']), trio(s.bgv, 'bgv', ['Pending', 'Cleared', 'Out']));
  cols.push(<td key="joined" className="px-3 py-2 text-center text-[13px] border-l border-[#EEF1F6]">
    {s.joined && s.joined.count ? <button type="button" className="hover:underline" onClick={() => onOpen && onOpen('joined', { tab: 'shortlisted' })}>{s.joined.count}</button> : '—'}</td>);
  return cols;
}

export function StageHeads({ maxRounds }) {
  const groups = [['Sourced', 'Pend.', 'Short.', 'Out'], ['Recruiter screen', 'Pend.', 'Passed', 'Out'], ['AI screen', 'Pend.', 'Short.', 'Out']];
  for (let i = 0; i < maxRounds; i += 1) groups.push([`Interview ${i + 1}`, 'Pend.', 'Passed', 'Out']);
  groups.push(['Offer', 'Pend.', 'Acc.', 'Out'], ['BGV / Drug test', 'Pend.', 'Cleared', 'Out']);
  return {
    top: [...groups.map((g) => <th key={g[0]} colSpan={3} scope="colgroup" className="px-2 py-2 text-center border-l border-[#E6E9F0]">{g[0]}</th>),
      <th key="joined" rowSpan={2} scope="col" className="px-3 py-2 text-center border-l border-[#E6E9F0]">Joined</th>],
    sub: groups.flatMap((g) => g.slice(1).map((x, i) => <th key={`${g[0]}.${x}`} scope="col" className={`px-2 py-1 text-center font-semibold normal-case ${i === 0 ? 'border-l border-[#E6E9F0]' : ''}`}>{x}</th>)),
  };
}
